import React, { useRef, useState } from "react";
import ReactDOMServer from 'react-dom/server';
import ChatComponent from "../../../../Components/ChatComponent/ChatComponent";
import ChatInput from "../../../../Components/ChatInput";
import { ReactComponent as VoiceBars } from "../../../../Assets/icon/voice-bars.svg";
import { Tooltip } from "react-tooltip";
import TooltipContent from "../../../../Components/Tooltip/ToolTipContent";
import GenericModal from "../../../../Components/Modal/GenericModal";
import { updateCoachingMode } from "../../../../Services/userSettings";
import { getUserProfile } from "../../../../Services/MyProfile";

/**
 * StandardChat - Default chat component for relationship view
 * @param {Object} props - Component props
 * @param {Array} props.chatData - Array of chat messages
 * @param {Function} props.onOptionSelected - Handler for option selection
 * @param {boolean} props.isAiLoading - Flag indicating if AI is loading
 * @param {string} props.transcript - Current transcript text
 * @param {string} props.chatChange - Current chat input value
 * @param {Function} props.clearManualData - Function to clear input
 * @param {string} props.lastQuestID - ID of the last question
 * @param {Function} props.handleEnterWithSubmit - Handler for enter key submission
 * @param {Function} props.onEnterDataPicker - Handler for data submission
 * @param {Function} props.afterOnPassData - Callback after data is passed
 * @param {Function} props.changeData - Handler for input changes
 * @param {boolean} props.restrictEnterManualData - Flag to restrict manual data entry
 * @param {boolean} props.isChatProgress - Flag indicating if chat is in progress
 * @param {boolean} props.isChatLoading - Flag indicating if chat is loading
 * @param {boolean} props.hideChatInput - Flag to hide chat input
 * @param {Function} props.loadLatestChatHistory - Function to load latest chat history
 * @param {string} props.chatMaxHeight - Maximum height for chat container
 * @param {boolean} props.isRecording - Flag indicating if recording is active
 * @param {boolean} props.isConnecting - Flag indicating if connecting to recording service
 * @param {Function} props.startRecording - Function to start/stop recording
 * @param {boolean} props.isPrivacyActivated - Flag indicating if privacy mode is active
 * @param {Function} props.onPrivacyClick - Handler for privacy toggle
 * @param {Function} props.handleNavigatetoElement - Handler for navigating to elements
 * @param {Function} props.setShowRelationShipContacts - Function to show/hide contacts modal
 * @param {React.RefObject} props.clearRef - Ref for clearing input
 * @param {React.RefObject} props.aiChatRef - Ref for AiChatComponent inside ChatComponent
 * @param {React.RefObject} props.inputContainerRef - Ref for input container
 * @param {React.RefObject} props.chatApiRef - Ref to control ChatComponent scroll API
 */
const StandardChat = ({
  chatData,
  onOptionSelected,
  isAiLoading,
  transcript,
  chatChange,
  clearManualData,
  lastQuestID,
  handleEnterWithSubmit,
  onEnterDataPicker,
  afterOnPassData,
  changeData,
  restrictEnterManualData,
  isChatProgress,
  isChatLoading,
  hideChatInput,
  loadLatestChatHistory,
  chatMaxHeight,
  isRecording,
  isConnecting,
  startRecording,
  isPrivacyActivated,
  onPrivacyClick,
  handleNavigatetoElement,
  setShowRelationShipContacts,
  clearRef,
  aiChatRef,
  inputContainerRef,
  chatApiRef,
  chatPlaceholder = "You can continue, clarify, or ask anything here…",
}) => {
  const chatOuterRef = useRef(null);
  const [showSwitchToVoice, setShowSwitchToVoice] = useState(false);

  const handleVoiceSwitch = () => {
      setShowSwitchToVoice(true);
  };

  const handleDirectSwitchToVoice = async () => {
    try {
      await updateCoachingMode('voice');
      localStorage.setItem('coaching_mode', 'voice');
      
      // Update profile
      try {
        const updatedProfile = await getUserProfile();
        localStorage.setItem('profile', JSON.stringify(updatedProfile));
      } catch (profileError) {
        console.error('Failed to refetch profile:', profileError);
        // Fallback: update local profile object
        try {
          const profileData = JSON.parse(localStorage.getItem('profile') || '{}');
          profileData.coaching_mode = 'voice';
          localStorage.setItem('profile', JSON.stringify(profileData));
        } catch (_) { }
      }
    } catch (_) {}
    // Navigate to voice mode
    window.location.href = '/dashboard';
  };
  return (
    <div className="relationship-chat-hero-container">
      <div
        className="relationShip-chats standard-chat-container"
        style={{ maxHeight: chatMaxHeight }}
        ref={chatOuterRef}
      >
        <ChatComponent
          chatArray={chatData}
          onOptionSelected={onOptionSelected}
          isAiLoading={isAiLoading}
          iselements={false}
          isSurvey={false}
          manualEnteredData={chatChange}
          clearManualData={clearManualData}
          lastQuestID={lastQuestID}
          handleEnterWithSubmit={handleEnterWithSubmit}
          ref={chatApiRef}
          aiChatRef={aiChatRef}
          isNewConversation={true}
        />
      </div>

      {!hideChatInput && (
        <div className="relationship-input-box-cntr">
          <div
            className="relationship-input-cntr added-margin"
            ref={inputContainerRef}
          >
            <div className="rl-input">
              <ChatInput
                onChange={onEnterDataPicker}
                value={transcript}
                afterOnPassData={afterOnPassData}
                changeData={changeData}
                ref={clearRef}
                restrictEnterManualData={restrictEnterManualData}
                isSurvey={false}
                isChatProgress={isChatProgress}
                isChatLoading={isChatLoading}
                loadLatestChatHistory={loadLatestChatHistory}
                showLastChat={true}
                isRecording={isRecording}
                isConnecting={isConnecting}
                startRecording={startRecording}
                isPrivacyActivated={isPrivacyActivated}
                chatPlaceHolder={chatPlaceholder}
              />
            </div>
          </div>

          <div className="relationship-chat-bottom-container">
            {chatData?.length === 0 ? (
              <div className="relationship-chat-bottom-container-left">
                {isPrivacyActivated && (
                  <div style={{
                    color: "#fff",
                    fontFamily: "Urbanist",
                    fontSize: 12,
                    lineHeight: "18px",
                    background: "rgba(255,255,255,0.2)",
                    padding: "6px 10px",
                    borderRadius: 999,
                    marginRight: 6
                  }}>
                    {"This Aris chat session is private and untracked. It won't appear in your history or shape future sessions. "}
                  </div>
                )}
              </div>
            ) : (
              <div className="relationship-chat-bottom-container-left">
                {isPrivacyActivated && (
                  <div style={{
                    color: "#fff",
                    fontFamily: "Urbanist",
                    fontSize: 12,
                    lineHeight: "18px",
                    background: "rgba(255,255,255,0.2)",
                    padding: "6px 10px",
                    borderRadius: 999,
                    marginRight: 6
                  }}>
                    {"This Aris chat session is private and untracked. It won’t appear in your history or shape future sessions. "}
                  </div>
                )}
              </div>
            )}
            <div className="relationship-chat-bottom-container-right">
              {/* Incognito toggle removed (product 2026-10-15). */}

              <div
                className="chat-voice-hero-cntr tooltip-container"
                onClick={handleVoiceSwitch}
                data-tooltip-id="relationship-tooltip-anchor-voice"
                data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
                  <div>Switch to voice mode</div>
                )}
              >
                <VoiceBars className="chat-lock-icon" />
                <Tooltip id="relationship-tooltip-anchor-voice" place="bottom" className="aris-tooltip" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Switch to Voice Mode modal - only shown during active session */}
      <GenericModal
        isOpen={showSwitchToVoice}
        onClose={() => setShowSwitchToVoice(false)}
        heading="Switch to Voice Mode?"
        primaryLabel="Switch"
        secondaryLabel="Cancel"
        onPrimary={async () => {
          await handleDirectSwitchToVoice();
          setShowSwitchToVoice(false);
        }}
        onSecondary={() => setShowSwitchToVoice(false)}
        maxWidth="548px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0 }}>
            By switching you will start a new voice session, but Aris will remember everything you talked about.
          </p>
        </div>
      </GenericModal>
    </div>
  );
};

export default StandardChat;
